import React, { useEffect } from "react";

import PromoBlock from "@/components/common/promoBlock";
import ScrollToTopButton from "@/components/common/scrollToTopButton";
import { ShowcaseHeadingLevelContext } from "@/components/common/showcaseBlock/headingLevelContext";
import { useImageSizes } from "@/contexts/ImageSizes";
import { ExpertsType } from "@/globalTypes";
import { useBanners } from "@/hooks/useBanners";
import { useExpertStatesPolling } from "@/hooks/useExpertStatesPolling";
import { useGetExperts } from "@/hooks/useGetExperts";
import { addImageSizes } from "@/utils/seo/imageSizes";
import { thumborizeHtml } from "@/utils/seo/thumbor";
import { cleanupFromDangerousTags, normalizeBodyHeadingsChain } from "@/utils/stringUtils";
import Breadcrumbs from "@components/common/Breadcrumbs";
import { BreadcrumbUrlPrefixEnum } from "@components/common/Breadcrumbs/types";
import SeoHeaders from "@components/common/seoHeaders";

import Constructor from "../constructor";
import { isShortHtml } from "../constructor/utils";
import ExpertsBlock from "../expertsBlock";
import LinkBlock from "../linkBlock";
import { LinkUrlPrefixEnum } from "../types";
import { getHubItemList, getLinkBlockObject } from "../utils";

import { CATEGORY_EXPERTS_STATE_UPDATE_INTERVAL_MS } from "./constants";
import { CategoryProps } from "./types";

import "../styles.scss";
import "./styles.scss";

const Category = ({ pageContext }: CategoryProps) => {
  const {
    seoId,
    title = "Статья на Lunaro",
    ogTitle,
    description = "Статья на Lunaro",
    textTop,
    textMiddle,
    textBottom,
    header,
    breadcrumbDisplayName,
    breadcrumbs,
    catalogLinks = [],
    categoryLinks = [],
    articleLinks = [],
    autolinkedCategoryLinks,
    url,
    expertsInitial,
    zone,
    commonTextTopBottomZone,
    commonTextMiddleBottomZone,
    commonBottomZone,
    wideBannerData,
  } = pageContext;
  const { checkIsWideBannerActive } = useBanners();
  const imageSizes = useImageSizes();

  const { experts, setExperts } = useGetExperts({ expertsInitial });
  const { expertsWithUpdatedStatuses } = useExpertStatesPolling<ExpertsType>({
    experts,
    pollingPeriodMs: CATEGORY_EXPERTS_STATE_UPDATE_INTERVAL_MS,
  });

  useEffect(() => {
    setExperts(expertsWithUpdatedStatuses);
  }, [expertsWithUpdatedStatuses, setExperts]);

  const wideBannerBannerIsShown = wideBannerData && checkIsWideBannerActive({
    isShown: wideBannerData.wideBannerIsShownOnCategory,
    isShownForUnauthorized: wideBannerData.wideBannerIsShownForUnauthorized,
    isShownForAuthorizedWithTrial: wideBannerData.wideBannerIsShownForAuthorizedWithTrial,
    isShownForAuthorizedWithoutTrial: wideBannerData.wideBannerIsShownForAuthorizedWithoutTrial,
  });

  const [bodyTop, bodyMiddle, bodyBottom] = normalizeBodyHeadingsChain(
    [textTop, textMiddle, textBottom].map((text) =>
      cleanupFromDangerousTags(text || "")),
  ).map((html) =>
    addImageSizes(thumborizeHtml(html), imageSizes));

  return (
    <ShowcaseHeadingLevelContext.Provider value="h2">
      <SeoHeaders
        title={title}
        ogTitle={ogTitle}
        description={description}
        url={url}
        imageUrl={`${textTop}${textMiddle}${textBottom}`?.match(/src\s*=\s*"(.+?)"/)?.[1]}
        itemList={getHubItemList(url, [...(zone ?? []), ...(commonBottomZone ?? [])])}
      />
      {wideBannerBannerIsShown && <PromoBlock wideBannerData={wideBannerData} />}
      <ScrollToTopButton />
      <div className="bg--white">
        <div className="page-width">
          <Breadcrumbs
            seoId={seoId}
            url={url}
            breadcrumbDisplayName={breadcrumbDisplayName || ""}
            breadcrumbs={breadcrumbs}
            urlPrefix={BreadcrumbUrlPrefixEnum.Category}
          />
          <h1 className="category__headline">
            {header || ""}
          </h1>
          <div
            className="seo-typography"
            dangerouslySetInnerHTML={{
              __html: bodyTop,
            }}
          />
          {/* картинку первого экрана может получить только эта зона: ниже блок экспертов */}
          <Constructor
            zone={commonTextTopBottomZone}
            expertsInitial={{ ...expertsInitial,
              edges: expertsInitial.edges?.map((e) =>
                ({ ...e, node: { ...e.node, isTrialAllowed: false } })) }}
            isImagePriorityAllowed={isShortHtml(textTop)}
          />
          <ExpertsBlock header="Тарологи Lunaro" experts={experts} />
          <div
            className="seo-typography"
            dangerouslySetInnerHTML={{
              __html: bodyMiddle,
            }}
          />
          <Constructor
            zone={commonTextMiddleBottomZone}
            expertsInitial={{ ...expertsInitial,
              edges: expertsInitial.edges?.map((e) =>
                ({ ...e, node: { ...e.node, isTrialAllowed: false } })) }}
            isImagePriorityAllowed={false}
          />
          <LinkBlock
            links={[
              ...getLinkBlockObject(catalogLinks, LinkUrlPrefixEnum.Catalog),
              ...getLinkBlockObject(categoryLinks, LinkUrlPrefixEnum.Category),
              ...getLinkBlockObject(articleLinks, LinkUrlPrefixEnum.Article),
            ]}
          />
          <div
            className="seo-typography"
            dangerouslySetInnerHTML={{
              __html: bodyBottom,
            }}
          />
          <Constructor
            zone={zone}
            expertsInitial={{ ...expertsInitial,
              edges: expertsInitial.edges?.map((e) =>
                ({ ...e, node: { ...e.node, isTrialAllowed: false } })) }}
            isImagePriorityAllowed={false}
          />
          <Constructor
            zone={commonBottomZone}
            expertsInitial={{ ...expertsInitial,
              edges: expertsInitial.edges?.map((e) =>
                ({ ...e, node: { ...e.node, isTrialAllowed: false } })) }}
            isImagePriorityAllowed={false}
          />
        </div>
      </div>
    </ShowcaseHeadingLevelContext.Provider>
  );
};

export default Category;
